import { reactive, watch, computed } from 'vue';
import AWS from 'aws-sdk';
import { fixedEncodeURIComponent } from '@/assets/js/general';

/** Persist only configuration — never full object lists or OAuth tokens */
const PERSIST_FILEMANAGER_KEYS = [
  'region',
  'delimiter',
  'currentBucket',
  'rememberedBuckets',
  'overrideBucketName',
  'currentDirectory',
  'mrap',
  'awsAccountId',
  'cognitoPoolId',
  'applicationClientId',
  'applicationLoginUrl',
  'identityPoolId',
  'userRoleId',
  'allowBucketSelection',
  'sharedSettings',
  'currentLocation',
  'initialized',
  'loggedOut',
  'autoLoginIn'
];

function loadPersistedFilemanagerPrefs() {
  try {
    if (typeof localStorage === 'undefined') return {};
    const raw = localStorage.getItem('s3console');
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out = {};
    for (const k of PERSIST_FILEMANAGER_KEYS) {
      if (Object.prototype.hasOwnProperty.call(parsed, k)) {
        out[k] = parsed[k];
      }
    }
    return out;
  } catch (e) {
    console.warn('Could not read s3console from localStorage', e);
    return {};
  }
}

const storedData = loadPersistedFilemanagerPrefs();

/** S3 list prefix: null = bucket root; strips leading/trailing slashes. */
export function normalizeFilemanagerDirectory(directory) {
  if (directory == null || directory === '') return null;
  const trimmed = String(directory).replace(/^\/+|\/+$/g, '');
  return trimmed || null;
}

/** `#path=...` from the URL fragment; `undefined` if the hash has no path param. */
export function getFilemanagerHashPath() {
  try {
    const hashData = new URLSearchParams(window.location.hash.slice(1));
    if (!hashData.has('path')) return undefined;
    return normalizeFilemanagerDirectory(hashData.get('path'));
  } catch (e) {
    return undefined;
  }
}

export function setFilemanagerHashPath(directory, router) {
  const norm = normalizeFilemanagerDirectory(directory);
  const nextHash = norm ? `#path=${fixedEncodeURIComponent(norm)}` : '';

  if (router?.currentRoute?.value) {
    if (router.currentRoute.value.hash !== nextHash) {
      router.replace({
        path: router.currentRoute.value.path,
        query: router.currentRoute.value.query,
        hash: nextHash,
      });
    }
    return;
  }

  if (window.location.hash !== nextHash) {
    const url = `${window.location.pathname}${window.location.search}${nextHash}`;
    history.replaceState(history.state, '', url);
  }
}

const hashDirectory = getFilemanagerHashPath();
const locationSpecific = {
  us: {
    region: 'us-east-1',
    currentBucket: 'storage-us.aderize.com',
    rememberedBuckets: ['storage-us.aderize.com'],
    identityPoolId: 'us-east-1:acdf6d44-a4d9-4c81-86e9-b7d9cbb55d21',
  },
  apac: {
    region: 'ap-southeast-1',
    currentBucket: 'storage.aderize.com',
    rememberedBuckets: ['storage.aderize.com'],
    identityPoolId: 'ap-southeast-1:2d8171cb-129c-41e7-9d0a-d7ae4b0a8cd2',
    
  }
};

if (process.env.APP_ENV === 'development') {
  locationSpecific.all = {
    region: 'eu-north-1',
    currentBucket: 'storage-test.aderize.com',
    rememberedBuckets: ['storage-test.aderize.com'],
    identityPoolId: 'eu-north-1:23833f50-4d6f-44b0-b03a-ae558ce0cc0a',

  }
}


const store = reactive(Object.assign({
    region: 'ap-southeast-1',

    delimiter: '/',
    currentBucket: 'storage.aderize.com',
    rememberedBuckets: ['storage.aderize.com'],
    overrideBucketName: 'storage.aderize.com',
    currentDirectory: hashDirectory !== undefined ? hashDirectory : null,

    mrap: 'storage-aderize-com-multiregion-ap',

    awsAccountId: '209570800564',
    cognitoPoolId: null,
    applicationClientId: null,
    applicationLoginUrl: null,
    identityPoolId: 'ap-southeast-1:2d8171cb-129c-41e7-9d0a-d7ae4b0a8cd2',
    userRoleId: null,

    objects: [],

    tokens: true,

    allowBucketSelection : false,

    loggedOut: false,
    autoLoginIn: false,
    sharedSettings: {},
    
    currentLocation: null
  }, storedData, {
    initialized: false,
    globalLoader: true,
    loggedOut: false,
    showBucketSelector: false,
    showSettings: false,
    showAddFolder: false,
    showTrash: false,
    showUploads: false,
    deletedObjects: {},
    copyObjects: [],
    moveObjects: []
}));

if (hashDirectory !== undefined) {
  store.currentDirectory = normalizeFilemanagerDirectory(hashDirectory);
}

export const setCorrectLocationParams = function(location) {
  if (location && (locationSpecific.all || locationSpecific[location])) {
    const locSpecific = locationSpecific.all || locationSpecific[location];
    Object.assign(store,locSpecific);
  }
}

export const getBuckets = function() {
  return store.rememberedBuckets.concat(store.sharedSettings?.buckets || [])
  .map(bucket => {
    if (typeof bucket === 'object') {
      return bucket;
    }

    return { bucket };
  }).sort((a, b) => a.bucket.localeCompare(b.bucket));
}

export const filemanager = {
  state: store,
  actions: {
    setCorrectLocationParams: setCorrectLocationParams
  }
};

watch(
  store,
  () => {
    if (store.autoUpdate) {
      store.autoUpdate = false;
      return;
    }
    try {
      const snapshot = {};
      for (const k of PERSIST_FILEMANAGER_KEYS) {
        snapshot[k] = store[k];
      }
      localStorage.setItem('s3console', JSON.stringify(snapshot));
    } catch (err) {
      console.error('Error saving file manager preferences', err);
    }
    AWS.config.update({ region: store.region, identityPoolId: store.identityPoolId });
  },
  { deep: true }
);

const currentBucket = computed(() => store.currentBucket);
watch(currentBucket, () => {
  store.deletedObjects = {};
});

export const s3StorageClasses = {
  STANDARD: 'Standard',
  STANDARD_IA: 'Standard IA',
  ONEZONE_IA: 'One Zone-IA',
  REDUCED_REDUNDANCY: 'Reduced Redundancy',
  GLACIER: 'Glacier',
  INTELLIGENT_TIERING: 'Intelligent Tiering',
  DEEP_ARCHIVE: 'Deep Archive'
};
